/**
 * BulletinUnsureView — /catalog-entry/bulletin/:vendor/:doc_no/unsure  불레틴 미확정 파트 페이지
 *
 * 한 편의 미확정 줄(unsure)마다 원문 칸 글 · 루틴 제안 · 근거를 보고 사람이 컨펌한다. 컨펌 하나 = 서버가 hp-bulletin 에 커밋 하나.
 * 위에 수리 기록(assemble_failed 편의 PR 링크). 레코드 페이지와 같은 꼴 — 편 하나에 줄이 수십이라 ③ 오른쪽 칸엔 안 들어간다.
 *
 * 분해: bulletin-unsure/types.ts(상태) · bulletin-unsure/render.ts(그리기, 순수) · 이 파일(핸들러 + 로드 + 부분 재렌더).
 * 주소창 값(vendor · doc_no)은 밖에서 온 값 — 생성자 한 곳에서 assert 로 거른다.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { assert } from '../lib/assert';
import type { BulletinUnsureData, UnsureLineKey } from './bulletin-unsure/types';
import { lineKeyOf, renderRepair, renderUnsureHead, renderUnsureRows } from './bulletin-unsure/render';

/** 주소창의 :vendor 는 밖에서 온 값 — 셋 중 하나인가 */
function isVendor(raw: string): raw is api.EVendor {
  return api.E_VENDORS.some((v) => v === raw);
}

/** 카드 · 버튼의 data-line — 우리가 lineKeyOf 로 그린 값이라 꼴이 맞아야 한다 */
function isLineKey(raw: string | undefined): raw is UnsureLineKey {
  return raw !== undefined && /^\d+:\d+$/.test(raw);
}

function isRowKind(raw: string): raw is api.ERowKind {
  return api.E_ROW_KINDS.some((k) => k === raw);
}

function isSkuBlock(raw: string): raw is api.ESkuBlock {
  return api.E_SKU_BLOCKS.some((b) => b === raw);
}

function isFixRest(raw: string): raw is api.EFixRest {
  return api.E_FIX_RESTS.some((r) => r === raw);
}

/** 가르기 없음 — 세 칸 다 null. 새로 고른 kind 와 제안의 fix 는 여기서 시작한다 */
const NO_CUT: api.FixCut = { text: null, subtitle: null, rest: null };

/** 새 kind 의 첫 고침 — sku 는 block 'cto', title 은 model '' 로 시작. 나머지는 kind 뿐. 가르기는 없음 */
function fixOfKind(kind: api.ERowKind): api.FixBody & api.FixCut {
  if (kind === 'sku')   return { kind, block: 'cto', ...NO_CUT };
  if (kind === 'title') return { kind, model: '', ...NO_CUT };
  return { kind, ...NO_CUT };
}

/** 제안이 fix 를 냈으면 그 줄의 chosenFix 로(가르기는 없음 — 루틴은 안 가른다) — 사람은 그대로 컨펌하거나 바꾼다 */
function chosenFixesOf(rows: api.UnsureView[]): Record<UnsureLineKey, api.FixBody & api.FixCut> {
  const chosen: Record<UnsureLineKey, api.FixBody & api.FixCut> = {};
  for (const view of rows) {
    if (view.proposal !== null && view.proposal.fix !== null) chosen[lineKeyOf(view)] = { ...view.proposal.fix, ...NO_CUT };
  }
  return chosen;
}

export class BulletinUnsureView extends SolaView<BulletinUnsureData> {
  override events = {
    change: {
      '@unsure-kind':  'handleKindChange',      // kind <select>
      '@unsure-field': 'handleFixFieldChange',  // block <select> · model <input>
    },
    click: {
      '@unsure-confirm': 'handleConfirm',       // 컨펌 버튼
    },
  };

  constructor(args: { vendor: string; doc_no: number }) {
    super();
    assert(isVendor(args.vendor), `주소창의 vendor 는 E_VENDORS 중 하나여야 한다. 받은 값: ${args.vendor}`);
    assert(Number.isInteger(args.doc_no) && args.doc_no > 0, `주소창의 doc_no 는 양의 정수여야 한다. 받은 값: ${args.doc_no}`);

    this.data = { key: { vendor: args.vendor, doc_no: args.doc_no }, doc: null, rows: [], repair: null, chosenFixes: {}, loading: true, error: '' };
  }

  override getTitle(): string {
    return `불레틴 ${this.data.key.doc_no} 미확정 — HCLTech 어드민`;
  }

  override onMount(): void {
    void this.loadUnsure();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-bulletin-unsure">
        <header class="page-header bu-page-header">
          <a class="btn-ghost" data-link href="/catalog-entry/bulletin">← 불레틴</a>
          <h1>불레틴 미확정 파트 — ${this.data.key.vendor} ${this.data.key.doc_no}</h1>
        </header>
        <section class="page-body bu-body">${this.renderBody()}</section>
      </div>
    `;
  }

  // ===== 핸들러 — 던지면 Sola 의 onActionError(alert) 가 받는다 =====

  /** kind <select> — 그 줄의 chosenFix 를 새 kind 로(sku 면 block 'cto', title 이면 model '' 로 시작). 서버 안 부름 */
  handleKindChange(_e: Event, el: HTMLElement): void {
    const line = el.dataset.line;
    assert(isLineKey(line), `kind select 의 data-line 은 "<table>:<row>" 여야 한다 — lineKeyOf 로 그렸다. 받은 값: ${line}`);
    assert(el instanceof HTMLSelectElement, 'unsure-kind 는 <select> 에만 붙인다');

    const chosenFixes = { ...this.data.chosenFixes };
    if (el.value === '') {
      delete chosenFixes[line];
    } else {
      assert(isRowKind(el.value), `kind select 의 값은 E_ROW_KINDS 중 하나여야 한다 — 그 배열로 option 을 그렸다. 받은 값: ${el.value}`);
      chosenFixes[line] = fixOfKind(el.value);
    }
    this.data = { ...this.data, chosenFixes };
    this.rerenderBody();
  }

  /** block <select> · model <input> — 그 줄의 chosenFix 의 그 칸만 바꾼다. 서버 안 부름 */
  handleFixFieldChange(_e: Event, el: HTMLElement): void {
    const line = el.dataset.line;
    assert(isLineKey(line), `field 의 data-line 은 "<table>:<row>" 여야 한다. 받은 값: ${line}`);
    assert(el instanceof HTMLSelectElement || el instanceof HTMLInputElement, 'unsure-field 는 <select> · <input> 에만 붙인다');
    const chosen = this.data.chosenFixes[line];
    assert(chosen !== undefined, `칸은 chosenFix 가 있는 줄에만 그린다 — ${line} 에 chosenFix 가 없는데 칸이 눌렸다`);

    const changed = this.fixWithField(chosen, el.dataset.field, el.value);
    this.data = { ...this.data, chosenFixes: { ...this.data.chosenFixes, [line]: changed } };
  }

  /** 컨펌 — 그 줄의 chosenFix 를 confirmFix 로. 돌아온 남은 줄로 rows 를 바꾸고 chosenFixes 에서 그 줄을 뺀다.
   *  chosenFix 가 없으면 assert(버튼이 비활성이라 있을 수 없다). title 인데 model 이 빈 글이면 alert 하고 안 보낸다.
   *  서버가 거르면(400 · 404 · 409) alert 하고 입력은 그대로 둔다 — 다시 누를 수 있다 */
  async handleConfirm(_e: Event, el: HTMLElement): Promise<void> {
    const line = el.dataset.line;
    assert(isLineKey(line), `컨펌 버튼의 data-line 은 "<table>:<row>" 여야 한다. 받은 값: ${line}`);
    const chosen = this.data.chosenFixes[line];
    assert(chosen !== undefined, `컨펌 버튼은 chosenFix 가 있을 때만 활성이다 — ${line} 에 chosenFix 가 없는데 눌렸다`);
    if (chosen.kind === 'title' && chosen.model.trim() === '') {
      alert('title 은 model(예: DL380 Gen11)을 적어야 컨펌할 수 있습니다.');
      return;
    }

    const [table, row] = line.split(':').map(Number);
    assert(table !== undefined && row !== undefined, `data-line "${line}" 이 "<table>:<row>" 꼴인데 둘로 안 갈렸다`);
    let rows: api.UnsureView[];
    try {
      rows = await api.confirmFix(this.data.key, table, row, chosen);
    } catch (err) {
      alert(`컨펌 실패 — ${err instanceof Error ? err.message : String(err)}`);
      return;
    }
    const { [line]: _done, ...chosenFixes } = this.data.chosenFixes;
    this.data = { ...this.data, rows, chosenFixes };
    this.rerenderBody();
  }

  // ===== 로드 · 재렌더 =====

  /** 그 편(fetchBulletinDoc) · 미확정 줄(fetchUnsureViews)을 같이 받고, 제안이 있는 줄마다 chosenFixes 를 제안의 fix 로 채운다 */
  private async loadUnsure(): Promise<void> {
    try {
      const [doc, unsure] = await Promise.all([api.fetchBulletinDoc(this.data.key), api.fetchUnsureViews(this.data.key)]);
      this.data = { ...this.data, doc, rows: unsure.rows, repair: unsure.repair, chosenFixes: chosenFixesOf(unsure.rows), loading: false, error: '' };
    } catch (err) {
      this.data = { ...this.data, loading: false, error: err instanceof Error ? err.message : '불러오기 실패' };
    }
    this.rerenderBody();
  }

  /** chosenFix 의 칸 하나를 새 값으로 — block · model(kind 에 딸린 칸) 또는 text · subtitle · rest(가르기. 빈 글이면 null).
   *  kind 에 없는 칸이 오면 assert — 칸은 그 kind 에만 그린다 */
  private fixWithField(chosen: api.FixBody & api.FixCut, field: string | undefined, value: string): api.FixBody & api.FixCut {
    if (chosen.kind === 'sku' && field === 'block') {
      assert(isSkuBlock(value), `block <select> 의 값은 E_SKU_BLOCKS 중 하나여야 한다. 받은 값: ${value}`);
      return { ...chosen, block: value };
    }
    if (chosen.kind === 'title' && field === 'model') {
      return { ...chosen, model: value };
    }
    if (field === 'text' || field === 'subtitle') {
      return { ...chosen, [field]: value.trim() === '' ? null : value.trim() };
    }
    if (field === 'rest') {
      assert(value === '' || isFixRest(value), `rest <select> 의 값은 비거나 E_FIX_RESTS 중 하나여야 한다. 받은 값: ${value}`);
      return { ...chosen, rest: value === '' ? null : value };
    }
    assert(false, `${chosen.kind} 줄엔 없는 칸(${field})이 눌렸다`);
  }

  private rerenderBody(): void {
    this.refreshRegion('.bu-body', () => this.renderBody());
  }

  private renderBody(): string {
    if (this.data.loading)      return `<div class="text-muted br-note">불러오는 중…</div>`;
    if (this.data.error)        return `<div class="ce-alert">${this.data.error}</div>`;
    if (this.data.doc === null) return `<div class="text-muted br-note">편이 없습니다.</div>`;

    return `
      ${renderUnsureHead(this.data)}
      ${renderRepair(this.data.repair)}
      ${renderUnsureRows(this.data)}`;
  }
}
